import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { AppButton, AppEmptyState, AppPillSwitch } from '../../components/ui';
import { formatMinutes } from '../../utils/tempsWeek';
import TempsDurationInput, { toEstimatedMinutes } from './TempsDurationInput';

const PERSON_COLORS = ['#2f6fb5', '#a4552c', '#2e7d6b', '#5f6a2a', '#b0463f', '#4d5fa0', '#8a6420', '#3f6f8f'];

const BRAND_META = {
  iti: { key: 'iti', label: 'itiConseil', tone: 'iti' },
  kkp: { key: 'kkp', label: 'Koikispass', tone: 'kkp' },
  klub: { key: 'klub', label: 'Klub', tone: 'klub' },
};

const DOLIBARR_WEB_BASE = (process.env.REACT_APP_DOLIBARR_URL || 'https://dolibarr2.iti-conseil.com').replace(/\/+$/, '');

const dolibarrCommandeUrl = (id) => `${DOLIBARR_WEB_BASE}/commande/card.php?id=${id}`;

const initials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
};

const colorForUser = (id) => {
  const n = Number(id) || 0;
  return PERSON_COLORS[Math.abs(n) % PERSON_COLORS.length];
};

const formatHt = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} € HT`;
};

const formatOrderDate = (iso) => {
  if (!iso) return 'sans date';
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
};

const isoWeekNumber = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const target = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNr = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNr + 3);
  const firstThursday = target.valueOf();
  target.setUTCMonth(0, 1);
  if (target.getUTCDay() !== 4) {
    target.setUTCMonth(0, 1 + ((4 - target.getUTCDay()) + 7) % 7);
  }
  return 1 + Math.ceil((firstThursday - target) / 604800000);
};

const taskWeekLabel = (task) => {
  if (task.remindLaterUntil) return 'Plus tard';
  if (!task.weekStart) return 'Sans semaine';
  const n = isoWeekNumber(task.weekStart);
  return n != null ? `Sem. ${n}` : task.weekStart;
};

const STATUS_OPTIONS = [
  { value: 'a_faire', label: 'À faire', tone: 'neutral' },
  { value: 'validee', label: 'Validée', tone: 'ok' },
  { value: 'archivee', label: 'Archivée', tone: 'neutral' },
];

const STATUS_BADGE = {
  a_faire: STATUS_OPTIONS[0],
  validee: STATUS_OPTIONS[1],
  archivee: STATUS_OPTIONS[2],
  en_cours: { label: 'En cours', tone: 'info' },
};

const orderStatusTone = (label = '') => {
  const l = label.toLowerCase();
  if (l.includes('cours')) return 'info';
  if (l.includes('valid')) return 'ok';
  return 'neutral';
};

const brandList = (brands = {}) => Object.values(BRAND_META).filter((b) => brands[b.key]);

const BrandDot = ({ tone }) => <span className={`tp-cmd__dot tp-cmd__dot--${tone}`} aria-hidden />;

const BrandPill = ({ brand }) => (
  <span className="tp-cmd__brand">
    <BrandDot tone={brand.tone} />
    {brand.label}
  </span>
);

const StatusBadge = ({ tone, label }) => (
  <span className={`tp-cmd__badge tp-cmd__badge--${tone}`}>{label}</span>
);

const StatusControl = ({ task, pending, onChangeStatus, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = STATUS_BADGE[task.status] || STATUS_BADGE.a_faire;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="tp-cmd__status-menu" ref={ref}>
      <button
        type="button"
        className={`tp-cmd__status-trigger tp-cmd__badge tp-cmd__badge--${current.tone}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Statut de la tâche"
        disabled={pending}
        onClick={() => setOpen((v) => !v)}
      >
        {pending ? <span className="tp-cmd__spinner tp-cmd__spinner--sm" aria-hidden /> : null}
        <span>{current.label}</span>
        <Icon icon="mdi:chevron-down" width={14} />
      </button>
      {open && (
        <div className="tp-cmd__status-dropdown" role="listbox" aria-label="Actions de la tâche">
          {STATUS_OPTIONS.map((opt) => {
            const active = task.status === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={active}
                className={`tp-cmd__status-option${active ? ' is-active' : ''}`}
                onClick={() => {
                  setOpen(false);
                  if (!active) onChangeStatus?.(task, opt.value);
                }}
              >
                <StatusBadge tone={opt.tone} label={opt.label} />
                {active ? <Icon icon="mdi:check" width={16} style={{ color: 'var(--app-accent)' }} /> : null}
              </button>
            );
          })}
          {onDelete ? (
            <>
              <div className="tp-cmd__status-sep" role="separator" />
              <button
                type="button"
                className="tp-cmd__status-option tp-cmd__status-option--danger"
                onClick={() => {
                  setOpen(false);
                  onDelete(task);
                }}
              >
                <span className="tp-cmd__status-delete">
                  <Icon icon="mdi:trash-can-outline" width={16} />
                  Supprimer
                </span>
              </button>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};

const matchesQuery = (order, q) => {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  if (String(order.id).includes(needle)) return true;
  if ((order.clientName || '').toLowerCase().includes(needle)) return true;
  return (order.productPreview || []).some((p) => String(p).toLowerCase().includes(needle));
};

const matchesBrand = (order, brand) => {
  if (!brand || brand === 'all') return true;
  return Boolean(order.brands?.[brand]);
};

const TempsCommandesBoard = ({
  orders = [],
  loading = false,
  syncing = false,
  lastSyncLabel = '',
  onSync,
  onToggleProd,
  onArchive,
  onCreateTask,
  onChangeStatus,
  onDeleteTask,
  pendingFlags = {},
  pendingStatuses = {},
}) => {
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState('prod');
  const [brand, setBrand] = useState('all');
  const [collapsedClients, setCollapsedClients] = useState({});
  const [openOrders, setOpenOrders] = useState({});
  const [adding, setAdding] = useState(null); // { orderId, lineId, title, amount, unit }
  const [archiving, setArchiving] = useState({});

  const baseOrders = useMemo(
    () => orders.filter((o) => matchesQuery(o, query) && matchesBrand(o, brand)),
    [orders, query, brand],
  );

  const counts = useMemo(() => {
    let prod = 0;
    let autres = 0;
    baseOrders.forEach((o) => {
      if (o.concernsProd) prod += 1;
      else autres += 1;
    });
    return { prod, autres, all: baseOrders.length };
  }, [baseOrders]);

  const filtered = useMemo(() => {
    if (scope === 'prod') return baseOrders.filter((o) => o.concernsProd);
    if (scope === 'autres') return baseOrders.filter((o) => !o.concernsProd);
    return baseOrders;
  }, [baseOrders, scope]);

  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((order) => {
      const key = String(order.socid || order.clientName || 'unknown');
      if (!map.has(key)) {
        map.set(key, {
          key,
          clientName: order.clientName || `Client #${order.socid}`,
          orders: [],
        });
      }
      map.get(key).orders.push(order);
    });
    return Array.from(map.values())
      .map((g) => {
        const totalHt = g.orders.reduce((s, o) => s + (Number(o.totalHt) || 0), 0);
        const prodCount = g.orders.filter((o) => o.concernsProd).length;
        return {
          ...g,
          totalHt,
          prodCount,
          countLabel: `${g.orders.length} commande${g.orders.length > 1 ? 's' : ''}`,
          prodLabel: prodCount > 0
            ? `${prodCount} prod`
            : 'hors prod',
        };
      })
      .sort((a, b) => a.clientName.localeCompare(b.clientName, 'fr'));
  }, [filtered]);

  const scopeOptions = [
    { value: 'prod', label: 'Prod', count: counts.prod },
    { value: 'autres', label: 'Autres', count: counts.autres },
    { value: 'all', label: 'Toutes', count: counts.all },
  ];

  const brandOptions = [
    { value: 'all', label: 'Toutes' },
    ...Object.values(BRAND_META).map((b) => ({
      value: b.key,
      label: (
        <span className="tp-cmd__brand-opt">
          <BrandDot tone={b.tone} />
          {b.label}
        </span>
      ),
    })),
  ];

  const emptyState = useMemo(() => {
    if (loading || groups.length > 0) return null;
    if (query.trim() && baseOrders.length === 0) {
      return {
        icon: 'mdi:magnify',
        title: 'Aucun résultat',
        text: `Aucune commande ne correspond à « ${query.trim()} ».`,
        action: 'Effacer la recherche',
        onAction: () => setQuery(''),
      };
    }
    if (scope === 'prod') {
      return {
        icon: 'mdi:check-circle-outline',
        title: 'Aucune commande ne concerne la prod',
        text: 'Les commandes ouvertes de ce filtre ne contiennent pas de produit itiConseil. Cochez « Prod » sur une commande pour l’ajouter ici.',
        action: `Voir les autres commandes (${counts.autres})`,
        onAction: () => setScope('autres'),
      };
    }
    if (scope === 'autres') {
      return {
        icon: 'mdi:check-circle-outline',
        title: 'Toutes les commandes concernent la prod',
        text: 'Aucune commande ouverte hors production pour ce filtre.',
        action: 'Voir les commandes prod',
        onAction: () => setScope('prod'),
      };
    }
    return {
      icon: 'mdi:package-variant',
      title: 'Aucune commande ouverte',
      text: 'Synchronisez Dolibarr pour importer les commandes validées ou en cours.',
      action: syncing ? null : 'Synchroniser Dolibarr',
      onAction: onSync,
    };
  }, [loading, groups.length, query, baseOrders.length, scope, counts.autres, syncing, onSync]);

  const toggleClient = (key) => {
    setCollapsedClients((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleOrder = (id) => {
    setOpenOrders((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const startAdd = (orderId, lineId) => {
    setAdding({ orderId, lineId, title: '', amount: '30', unit: 'min' });
  };

  const submitAdd = async () => {
    if (!adding || !adding.title.trim()) return;
    const estimatedMinutes = toEstimatedMinutes(adding.amount, adding.unit);
    await onCreateTask?.({
      commandeId: adding.orderId,
      ligneId: adding.lineId,
      title: adding.title.trim(),
      estimatedMinutes: Number.isFinite(estimatedMinutes) ? estimatedMinutes : 0,
      assigneeId: null,
      weekStart: null,
    });
    setAdding(null);
  };

  const handleArchive = async (order) => {
    setArchiving((prev) => ({ ...prev, [order.id]: true }));
    try {
      await onArchive?.(order);
    } finally {
      setArchiving((prev) => ({ ...prev, [order.id]: false }));
    }
  };

  const showColHead = !loading && groups.length > 0;

  return (
    <div className="tp-cmd">
      <div className="tp-cmd__toolbar">
        <div className="tp-cmd__filters">
          <label className="tp-cmd__search">
            <Icon icon="mdi:magnify" width={16} aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Rechercher un client ou un n° de commande"
              placeholder="Client ou n° de commande"
            />
            {query ? (
              <button
                type="button"
                className="tp-cmd__search-clear"
                aria-label="Effacer la recherche"
                onClick={() => setQuery('')}
              >
                <Icon icon="mdi:close" width={16} />
              </button>
            ) : null}
          </label>
          <AppPillSwitch
            variant="segment"
            options={scopeOptions}
            value={scope}
            onChange={setScope}
            ariaLabel="Vue"
          />
          <AppPillSwitch
            variant="segment"
            options={brandOptions}
            value={brand}
            onChange={setBrand}
            ariaLabel="Marque"
          />
        </div>
        <div className="tp-cmd__sync">
          <AppButton
            variant="outline-secondary"
            icon={syncing ? undefined : 'mdi:sync'}
            loading={syncing}
            loadingLabel="Synchronisation…"
            onClick={onSync}
            disabled={syncing}
          >
            Synchroniser Dolibarr
          </AppButton>
          {lastSyncLabel ? <span className="tp-cmd__sync-sub">{lastSyncLabel}</span> : null}
        </div>
      </div>

      {syncing ? (
        <div className="tp-cmd__sync-bar" aria-hidden>
          <div className="tp-cmd__sync-bar-fill" />
        </div>
      ) : null}

      {loading ? (
        <AppEmptyState loading />
      ) : emptyState ? (
        <div className="tp-cmd__empty">
          <div className="tp-cmd__empty-icon">
            <Icon icon={emptyState.icon} width={22} />
          </div>
          <div className="tp-cmd__empty-title">{emptyState.title}</div>
          <div className="tp-cmd__empty-text">{emptyState.text}</div>
          {emptyState.action && emptyState.onAction ? (
            <AppButton variant="outline-secondary" onClick={emptyState.onAction}>
              {emptyState.action}
            </AppButton>
          ) : null}
        </div>
      ) : (
        <>
          {showColHead ? (
            <div className="tp-cmd__cols" aria-hidden>
              <span className="tp-cmd__col-prod">Prod</span>
              <span className="tp-cmd__col-order">Commande</span>
              <span className="tp-cmd__col-brands">Marques</span>
              <span className="tp-cmd__col-amount">Montant</span>
              <span className="tp-cmd__col-tasks">Tâches</span>
              <span className="tp-cmd__col-actions" />
            </div>
          ) : null}

          {groups.map((group) => {
            const open = collapsedClients[group.key] !== true;
            return (
              <section key={group.key} aria-label={group.clientName}>
                <button
                  type="button"
                  className="tp-cmd__client"
                  aria-expanded={open}
                  onClick={() => toggleClient(group.key)}
                >
                  <Icon
                    icon="mdi:chevron-down"
                    width={16}
                    className={`tp-cmd__chevron${open ? ' is-open' : ''}`}
                  />
                  <span className="tp-cmd__client-main">
                    <span className="tp-cmd__client-name">{group.clientName}</span>
                    <span className="tp-cmd__client-count">{group.countLabel}</span>
                  </span>
                  <span className="tp-cmd__client-prod">{group.prodLabel}</span>
                  <span className="tp-cmd__client-total">{formatHt(group.totalHt)}</span>
                </button>

                {open && group.orders.map((order) => {
                  const detailOpen = Boolean(openOrders[order.id]);
                  const summary = order.taskSummary || {};
                  const totalTasks = Number(summary.total) || 0;
                  const done = Number(summary.done) || 0;
                  const todo = Number(summary.todo) || 0;
                  const taskPct = totalTasks > 0 ? Math.round((done / totalTasks) * 100) : 0;
                  const isProd = Boolean(order.concernsProd);
                  const pend = Boolean(pendingFlags[order.id]);
                  const brands = brandList(order.brands);
                  const summaryText = (order.productPreview || []).join(' · ');
                  const taskLabel = totalTasks === 0
                    ? 'Aucune tâche'
                    : `${todo} à faire · ${done} validée${done > 1 ? 's' : ''}`;
                  const canArchive = done > 0;
                  const isArchiving = Boolean(archiving[order.id]);

                  return (
                    <div
                      key={order.id}
                      className={`tp-cmd__order${isProd ? '' : ' is-dim'}${detailOpen ? ' is-open' : ''}`}
                    >
                      <div className="tp-cmd__order-row">
                        <button
                          type="button"
                          role="checkbox"
                          aria-checked={isProd}
                          aria-label="Concerne la prod"
                          title="Concerne la prod"
                          className="tp-cmd__prod-btn"
                          disabled={pend}
                          onClick={() => onToggleProd?.(order, !isProd)}
                        >
                          {pend ? (
                            <span className="tp-cmd__spinner" aria-hidden />
                          ) : (
                            <span className={`tp-cmd__check${isProd ? ' is-checked' : ''}`}>
                              {isProd ? <Icon icon="mdi:check" width={14} /> : null}
                            </span>
                          )}
                        </button>

                        <button
                          type="button"
                          className="tp-cmd__order-main"
                          aria-expanded={detailOpen}
                          onClick={() => toggleOrder(order.id)}
                        >
                          <span className="tp-cmd__order-top">
                            <span className="tp-cmd__order-ref">#{order.id}</span>
                            <StatusBadge
                              tone={orderStatusTone(order.statutLabel)}
                              label={order.statutLabel || '—'}
                            />
                            <span className="tp-cmd__order-date">{formatOrderDate(order.date)}</span>
                          </span>
                          {summaryText ? (
                            <span className="tp-cmd__order-summary" title={summaryText}>{summaryText}</span>
                          ) : null}
                        </button>

                        <div className="tp-cmd__col-brands tp-cmd__brands">
                          {brands.map((b) => <BrandPill key={b.key} brand={b} />)}
                        </div>

                        <span className="tp-cmd__col-amount tp-cmd__amount">{formatHt(order.totalHt)}</span>

                        <div className="tp-cmd__col-tasks tp-cmd__task-meta">
                          <span>{taskLabel}</span>
                          {totalTasks > 0 ? (
                            <div className="tp-cmd__progress" aria-hidden>
                              <div className="tp-cmd__progress-fill" style={{ width: `${taskPct}%` }} />
                            </div>
                          ) : null}
                        </div>

                        <div className="tp-cmd__col-actions tp-cmd__order-actions">
                          <a
                            href={dolibarrCommandeUrl(order.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tp-cmd__dolibarr"
                          >
                            Dolibarr
                            <Icon icon="mdi:open-in-new" width={13} />
                          </a>
                          <button
                            type="button"
                            className="tp-cmd__icon-btn"
                            aria-label="Détail de la commande"
                            aria-expanded={detailOpen}
                            onClick={() => toggleOrder(order.id)}
                          >
                            <Icon
                              icon="mdi:chevron-down"
                              width={16}
                              className={`tp-cmd__chevron${detailOpen ? ' is-open' : ''}`}
                            />
                          </button>
                        </div>
                      </div>

                      {detailOpen ? (
                        <div className="tp-cmd__detail">
                          <div className="tp-cmd__detail-panel">
                            {(order.lines || []).map((line) => {
                              const lineKey = `${order.id}-${line.id || 'extra'}`;
                              const lineBrands = brandList(line.brands);
                              const primaryBrand = lineBrands[0];
                              const isAdding = adding
                                && adding.orderId === order.id
                                && String(adding.lineId) === String(line.id);
                              const tasks = line.tasks || [];
                              const canAdd = Boolean(line.id) && isProd;

                              return (
                                <div key={lineKey} className="tp-cmd__line">
                                  <div className="tp-cmd__line-head">
                                    <Icon icon="mdi:package-variant" width={15} className="tp-cmd__line-icon" />
                                    <span className="tp-cmd__line-label">{line.productLabel}</span>
                                    {primaryBrand ? <BrandPill brand={primaryBrand} /> : null}
                                    <span className="tp-cmd__line-qty">
                                      {line.qty != null ? `× ${line.qty}` : ''}
                                    </span>
                                    {canAdd ? (
                                      <AppButton
                                        size="sm"
                                        variant="outline-secondary"
                                        icon="mdi:plus"
                                        onClick={() => startAdd(order.id, line.id)}
                                      >
                                        Tâche
                                      </AppButton>
                                    ) : null}
                                  </div>

                                  {tasks.map((task) => {
                                    const who = task.assignee;
                                    const statusPending = Boolean(pendingStatuses[task.id]);
                                    return (
                                      <div key={task.id} className="tp-cmd__task">
                                        <span className="tp-cmd__task-title">
                                          <span className="tp-cmd__task-tree" aria-hidden />
                                          <span className="text-truncate">{task.title}</span>
                                        </span>
                                        <span className={`tp-cmd__task-who${!who ? ' is-empty' : ''}`}>
                                          {who ? (
                                            <span
                                              className="tp-cmd__avatar"
                                              style={{ background: colorForUser(who.id) }}
                                            >
                                              {initials(who.name)}
                                            </span>
                                          ) : (
                                            <span className="tp-cmd__avatar tp-cmd__avatar--ghost" />
                                          )}
                                          <span className="text-truncate">{who?.name || 'Non assignée'}</span>
                                        </span>
                                        <span className="tp-cmd__task-week">{taskWeekLabel(task)}</span>
                                        <span>
                                          <StatusControl
                                            task={task}
                                            pending={statusPending}
                                            onChangeStatus={onChangeStatus}
                                            onDelete={onDeleteTask}
                                          />
                                        </span>
                                        <span className="tp-cmd__task-time">
                                          {formatMinutes(task.estimatedMinutes)}
                                          <span className="sep"> / </span>
                                          <span className={task.actualMinutes ? 'done' : ''}>
                                            {task.actualMinutes != null
                                              ? formatMinutes(task.actualMinutes)
                                              : '—'}
                                          </span>
                                        </span>
                                      </div>
                                    );
                                  })}

                                  {tasks.length === 0 ? (
                                    <div className="tp-cmd__line-empty">Aucune tâche sur cette ligne</div>
                                  ) : null}

                                  {isAdding ? (
                                    <div className="tp-cmd__add">
                                      <input
                                        autoFocus
                                        className="tp-cmd__add-title"
                                        aria-label="Titre de la tâche"
                                        placeholder="Titre de la tâche"
                                        value={adding.title}
                                        onChange={(e) => setAdding((s) => ({ ...s, title: e.target.value }))}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') submitAdd();
                                          if (e.key === 'Escape') setAdding(null);
                                        }}
                                      />
                                      <TempsDurationInput
                                        className="tp-cmd__add-duration"
                                        value={adding.amount}
                                        unit={adding.unit}
                                        prefix={<span className="tp-cmd__add-duration-label">Prévu</span>}
                                        ariaLabel="Durée prévue"
                                        onChange={({ amount, unit }) => setAdding((s) => ({ ...s, amount, unit }))}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') submitAdd();
                                          if (e.key === 'Escape') setAdding(null);
                                        }}
                                      />
                                      <AppButton
                                        size="sm"
                                        disabled={!adding.title.trim()}
                                        onClick={submitAdd}
                                      >
                                        Ajouter
                                      </AppButton>
                                      <AppButton
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={() => setAdding(null)}
                                      >
                                        Annuler
                                      </AppButton>
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })}

                            <div className="tp-cmd__detail-foot">
                              {!isProd ? (
                                <span className="tp-cmd__foot-hint">
                                  <Icon icon="mdi:information-outline" width={13} />
                                  Hors prod : cochez « Prod » pour ajouter des tâches.
                                </span>
                              ) : <span />}
                              <AppButton
                                size="sm"
                                variant="outline-secondary"
                                icon="mdi:archive-outline"
                                disabled={!canArchive || isArchiving}
                                loading={isArchiving}
                                loadingLabel="Archivage…"
                                onClick={() => handleArchive(order)}
                                title="Archive les tâches validées de cette commande"
                              >
                                {canArchive
                                  ? `Archiver les validées (${done})`
                                  : 'Aucune validée'}
                              </AppButton>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </section>
            );
          })}
        </>
      )}
    </div>
  );
};

export default TempsCommandesBoard;
